import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Star, Users, Copy } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;

// Format E.164 → "(951) 290-8133"
const fmtPhone = (p) => {
  if (!p) return "";
  const d = String(p).replace(/\D/g, "").replace(/^1/, "").slice(-10);
  if (d.length !== 10) return String(p);
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};
const telHref = (p) => {
  const d = String(p || "").replace(/\D/g, "").replace(/^1/, "").slice(-10);
  return d.length === 10 ? `+1${d}` : String(p || "");
};

export default function ReceiptView() {
  const { invoiceId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/invoices/${invoiceId}/public-receipt`);
        if (!cancelled) setReceipt(data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || "Receipt not available");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [invoiceId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center" data-testid="receipt-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#207024]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="receipt-error">
        <div className="max-w-md text-center">
          <div className="text-lg font-bold text-zinc-800 mb-2">Receipt not available</div>
          <div className="text-sm text-zinc-500">{error}</div>
        </div>
      </div>
    );
  }

  const paidWhen = receipt.paid_at ? new Date(receipt.paid_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "";

  const quoteOrigin = window.location.origin;
  const referralText = `Hey! I just had ScreenzZ do work on my screens — super quick and hassle-free. If you need any screen work, they have a free instant quote in under a minute: ${quoteOrigin}/instant-estimate — highly recommend!`;

  const shareReferral = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text: referralText });
        return;
      } catch (_err) {
        // fall through to clipboard copy
      }
    }
    navigator.clipboard?.writeText(referralText);
    toast.success("Referral text copied — paste it into iMessage / WhatsApp / anywhere");
  };

  return (
    <div className="min-h-screen bg-zinc-50 pb-16" data-testid="receipt-view">
      <div className="max-w-lg mx-auto px-4 pt-8">
        <div className="flex flex-col items-center text-center gap-3 mb-6">
          <img
            src="https://customer-assets.emergentagent.com/job_tech-dispatch-31/artifacts/a6ta9f48_SCREENZZ-LOGO-RGB_TextOnly.png"
            alt="ScreenzZ"
            className="h-12 w-auto"
          />
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider">
            <CheckCircle2 className="w-4 h-4" /> Paid
          </div>
          <h1 className="text-2xl font-bold text-zinc-900">Thank you, {receipt.customer_name}!</h1>
          <p className="text-sm text-zinc-600">Your payment was received successfully.</p>
        </div>

        {/* PRIMARY CTA — Review, top of page */}
        {receipt.review_url && (
          <a
            href={receipt.review_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="receipt-review-cta"
            className="block rounded-2xl overflow-hidden shadow-md bg-gradient-to-br from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 active:from-amber-600 active:to-amber-700 transition-colors mb-5"
          >
            <div className="px-5 py-5 flex items-center gap-4">
              <div className="flex-shrink-0 w-14 h-14 rounded-full bg-white/25 flex items-center justify-center">
                <Star className="w-8 h-8 text-white fill-white" />
              </div>
              <div className="flex-1 text-white">
                <div className="text-lg font-bold leading-tight">Loved us? Leave a 5★ review</div>
                <div className="text-xs text-amber-50/90 mt-1">Takes 15 seconds &amp; means the world to a small biz.</div>
              </div>
              <div className="text-white text-2xl">›</div>
            </div>
          </a>
        )}

        {/* Receipt details */}
        <div className="bg-white rounded-2xl border-2 border-zinc-200 shadow-sm p-5 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-zinc-500">Invoice</span>
            <span className="font-mono font-semibold" data-testid="receipt-invoice-number">{receipt.invoice_number}</span>
          </div>
          {paidWhen && (
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Paid</span>
              <span className="font-semibold">{paidWhen}</span>
            </div>
          )}
          {receipt.payment_method && (
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Method</span>
              <span className="font-semibold capitalize">{receipt.payment_method.replace(/_/g, " ")}</span>
            </div>
          )}

          <div className="border-t border-zinc-200 pt-3 space-y-2">
            {receipt.line_items.map((li, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-zinc-800">
                  {li.name} <span className="text-zinc-400">× {li.quantity}</span>
                </span>
                <span className="font-semibold">{fmt(li.line_total)}</span>
              </div>
            ))}
          </div>

          <div className="border-t border-zinc-200 pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span>{fmt(receipt.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Tax</span><span>{fmt(receipt.tax)}</span></div>
            <div className="flex justify-between text-lg font-bold text-[#207024] pt-1"><span>Total Paid</span><span data-testid="receipt-total">{fmt(receipt.total)}</span></div>
          </div>
        </div>

        {/* SECONDARY CTA — Referral, bottom of page */}
        <div className="bg-white rounded-2xl border-2 border-emerald-200 shadow-sm p-5 mt-5" data-testid="receipt-referral-block">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-700" />
            </div>
            <div className="text-base font-bold text-zinc-900">Know someone who needs screens?</div>
          </div>
          <p className="text-sm text-zinc-600 mb-3">
            Send a friend or family member our instant quote link — takes them &lt;1 minute.
          </p>
          <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-700 leading-relaxed" data-testid="receipt-referral-text">
            {referralText}
          </div>
          <button
            onClick={shareReferral}
            data-testid="receipt-referral-share"
            className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] active:bg-[#164e19] text-white font-semibold text-sm transition-colors"
          >
            <Copy className="w-4 h-4" /> Copy &amp; send this text
          </button>
        </div>

        <p className="text-center text-xs text-zinc-500 mt-6">
          Questions? Call or text{" "}
          <a href={`tel:${telHref(receipt.business_phone)}`} className="text-[#207024] font-semibold">
            {fmtPhone(receipt.business_phone) || "us"}
          </a>{" "}
          — we&apos;re happy to help.
        </p>
      </div>
    </div>
  );
}
